import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const sections = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'description', title: '2. Description of Service' },
  { id: 'accounts', title: '3. User Accounts and Registration' },
  { id: 'subscriptions', title: '4. Subscription Plans and Billing' },
  { id: 'payments', title: '5. Payment Processing' },
  { id: 'cancellation', title: '6. Cancellation and Refund Policy' },
  { id: 'ip', title: '7. Intellectual Property' },
  { id: 'ai', title: '8. AI-Generated Coaching' },
  { id: 'acceptable-use', title: '9. Acceptable Use Policy' },
  { id: 'data', title: '10. Data Storage and Security' },
  { id: 'liability', title: '11. Limitation of Liability' },
  { id: 'law', title: '12. Governing Law' },
  { id: 'contact', title: '13. Contact' },
];

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#e5e5e5' }}>
      <style>{`
        .legal-h2 { font-family: 'Courier Prime', monospace; font-weight: 700; font-size: 1.05rem; text-transform: uppercase; letter-spacing: 0.1em; color: #E8A020; margin-top: 2.5rem; margin-bottom: 0.75rem; }
        .legal-p { font-family: 'Inter', sans-serif; font-size: 0.88rem; color: #b0b0b0; line-height: 1.75; margin-bottom: 0.9rem; }
        .legal-li { font-family: 'Inter', sans-serif; font-size: 0.88rem; color: #b0b0b0; line-height: 1.75; margin-bottom: 0.4rem; }
        .legal-toc-link { font-family: 'Inter', sans-serif; font-size: 0.82rem; color: #808080; text-decoration: none; display: block; padding: 4px 0; transition: color 0.18s; }
        .legal-toc-link:hover { color: #E8A020; }
        .legal-back { background: none; border: none; color: #606060; font-family: 'Courier Prime', monospace; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.14em; cursor: pointer; padding: 0; display: flex; align-items: center; gap: 6px; transition: color 0.18s; }
        .legal-back:hover { color: #E8A020; }
      `}</style>

      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(232,160,32,0.1)', padding: '16px clamp(16px,4vw,48px)', position: 'sticky', top: 0, background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(12px)', zIndex: 40 }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button className="legal-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1rem', color: '#E8A020', letterSpacing: '0.14em' }}>
            PANGAJI
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: 'clamp(32px,6vw,64px) clamp(16px,4vw,48px)' }}>
        {/* Hero */}
        <div style={{ marginBottom: '48px' }}>
          <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.25em', color: '#E8A020', marginBottom: '12px' }}>
            Legal
          </p>
          <h1 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: 'clamp(1.8rem,4vw,2.8rem)', letterSpacing: '-0.02em', lineHeight: 1.1, marginBottom: '16px' }}>
            Terms of Service
          </h1>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#606060' }}>
            Last updated: May 2026 &nbsp;·&nbsp; Effective date: May 2026
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2.5fr)', gap: '48px', alignItems: 'start' }}>
          {/* Table of contents */}
          <div style={{ position: 'sticky', top: '80px', background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.06)', padding: '20px' }}>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.22em', color: '#606060', marginBottom: '14px' }}>
              Contents
            </p>
            {sections.map(s => (
              <a key={s.id} href={`#${s.id}`} className="legal-toc-link">{s.title}</a>
            ))}
          </div>

          {/* Body */}
          <div>
            <section id="acceptance">
              <h2 className="legal-h2">1. Acceptance of Terms</h2>
              <p className="legal-p">
                By accessing or using Pangaji ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
              </p>
              <p className="legal-p">
                These Terms form a legally binding agreement between you and LAJ House Limited ("we," "us," or "our"), a company registered in Kenya (PVT-3B15M26E).
              </p>
            </section>

            <section id="description">
              <h2 className="legal-h2">2. Description of Service</h2>
              <p className="legal-p">
                Pangaji is a story development platform designed to help filmmakers, screenwriters, and storytellers build, develop, and refine their projects. The Service provides structured tools for character development, plot construction, world building, and AI-powered coaching ("AI Mentor").
              </p>
              <p className="legal-p">
                Pangaji is intended for use by independent and professional filmmakers operating primarily in Africa. The Service is available online via subscription.
              </p>
            </section>

            <section id="accounts">
              <h2 className="legal-h2">3. User Accounts and Registration</h2>
              <p className="legal-p">
                To use the Service, you must register for an account by providing a valid email address, a name, and a password. You agree to provide accurate and complete information and to keep your account credentials confidential.
              </p>
              <p className="legal-p">
                You are responsible for all activity that occurs under your account. If you suspect unauthorised access, you must notify us immediately at jamdany@lajhouseltd.com.
              </p>
              <p className="legal-p">
                You must be at least 13 years old to use the Service. By registering, you represent that you meet this age requirement.
              </p>
            </section>

            <section id="subscriptions">
              <h2 className="legal-h2">4. Subscription Plans and Billing</h2>
              <p className="legal-p">
                Pangaji offers the following subscription tiers:
              </p>
              <ul style={{ margin: '0 0 0.9rem 1.2rem', padding: 0 }}>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Spark</strong> — Free. Limited features for getting started.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Indie</strong> — KES 499 per month. For individual creators.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Studio</strong> — KES 1,299 per month. For writers' rooms and series work.</li>
                <li className="legal-li"><strong style={{ color: '#e5e5e5' }}>Founding</strong> — KES 4,999, paid once. Permanent Studio access for the life of the Service; limited to 250 seats and one seat per account. Not a subscription: nothing renews.</li>
              </ul>
              <p className="legal-p">
                All prices are in Kenyan shillings (KES) and charged in KES. Your invoice will reflect the amount charged at the time of payment.
              </p>
              <p className="legal-p">
                Subscriptions auto-renew at the end of each billing period unless cancelled. You authorise us to charge your selected payment method for each renewal until you cancel.
              </p>
            </section>

            <section id="payments">
              <h2 className="legal-h2">5. Payment Processing</h2>
              <p className="legal-p">
                Payments are processed by Paystack (a licensed payment service provider). We accept M-Pesa (via Safaricom) and major debit/credit cards (Visa, Mastercard).
              </p>
              <p className="legal-p">
                We do not store your card details. All payment information is handled directly by Paystack in accordance with PCI DSS standards. By providing your payment details, you agree to Paystack's terms of service.
              </p>
            </section>

            <section id="cancellation">
              <h2 className="legal-h2">6. Cancellation and Refund Policy</h2>
              <p className="legal-p">
                You may cancel your subscription at any time from your account's billing settings. Upon cancellation, you retain access to your paid plan's features until the end of the current billing period.
              </p>
              <p className="legal-p">
                We do not offer pro-rata refunds for unused portions of a billing period. If you cancel mid-cycle, you will continue to have access until the end of that cycle and will not be charged for the next period.
              </p>
              <p className="legal-p">
                Founding membership is a one-time purchase and is not refundable once your seat is confirmed, except where required by law. If a Founding payment cannot be seated (for example, every seat was claimed before your payment completed), it is refunded in full automatically. If a Founding payment is refunded or reversed, the seat is released and your account returns to the plan you held before.
              </p>
              <p className="legal-p">
                If you believe a charge was made in error, contact us at jamdany@lajhouseltd.com within 30 days of the charge and we will investigate.
              </p>
            </section>

            <section id="ip">
              <h2 className="legal-h2">7. Intellectual Property</h2>
              <p className="legal-p">
                You retain full ownership of all story content you create using Pangaji — including characters, plots, world building documents, and any other written material. We make no claim of ownership over your creative work.
              </p>
              <p className="legal-p">
                The Pangaji platform itself — including its software, interface, brand assets, and proprietary features — is owned by LAJ House Limited and protected by applicable intellectual property laws. You may not copy, modify, or redistribute the platform or its components.
              </p>
              <p className="legal-p">
                By using the Service, you grant us a limited, non-exclusive licence to store and process your content solely as necessary to provide the Service to you.
              </p>
            </section>

            <section id="ai">
              <h2 className="legal-h2">8. AI-Generated Coaching</h2>
              <p className="legal-p">
                Pangaji includes an AI Mentor feature that provides coaching, feedback, and story development prompts based on your project data. These responses are generated by Google Gemini, a third-party AI model.
              </p>
              <p className="legal-p">
                All written output belongs to you. The AI Mentor is a coaching and feedback tool — it does not write content on your behalf and does not claim authorship of any material you produce on the platform.
              </p>
              <p className="legal-p">
                AI-generated coaching responses are not a substitute for professional script consultation, legal advice, or other specialist guidance. We do not guarantee the accuracy or suitability of AI outputs.
              </p>
              <p className="legal-p">
                Your story content may be sent to the Google Gemini API to generate coaching responses. This content is not used to train AI models. See our Privacy Policy for more details.
              </p>
            </section>

            <section id="acceptable-use">
              <h2 className="legal-h2">9. Acceptable Use Policy</h2>
              <p className="legal-p">You agree not to use Pangaji to:</p>
              <ul style={{ margin: '0 0 0.9rem 1.2rem', padding: 0 }}>
                <li className="legal-li">Create or distribute content that is unlawful, defamatory, harassing, or abusive.</li>
                <li className="legal-li">Infringe the intellectual property rights of third parties.</li>
                <li className="legal-li">Attempt to gain unauthorised access to any part of the Service or another user's account.</li>
                <li className="legal-li">Reverse-engineer, decompile, or otherwise attempt to extract source code from the platform.</li>
                <li className="legal-li">Use the Service for any commercial purpose not expressly permitted by these Terms.</li>
                <li className="legal-li">Upload malware, viruses, or any code designed to disrupt or damage the Service.</li>
              </ul>
              <p className="legal-p">
                We reserve the right to suspend or terminate accounts that violate this policy without prior notice.
              </p>
            </section>

            <section id="data">
              <h2 className="legal-h2">10. Data Storage and Security</h2>
              <p className="legal-p">
                Your data is stored on MongoDB Atlas, with servers hosted in the United States on Amazon Web Services (AWS). Data is encrypted in transit (TLS) and at rest.
              </p>
              <p className="legal-p">
                We implement industry-standard security measures to protect your information. However, no system is completely secure, and we cannot guarantee absolute security.
              </p>
              <p className="legal-p">
                For full details on how we collect, use, and protect your data, please read our <a href="/privacy" style={{ color: '#E8A020', textDecoration: 'none' }}>Privacy Policy</a>.
              </p>
            </section>

            <section id="liability">
              <h2 className="legal-h2">11. Limitation of Liability</h2>
              <p className="legal-p">
                To the maximum extent permitted by applicable Kenyan law, LAJ House Limited shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Service.
              </p>
              <p className="legal-p">
                Our total liability to you for any claim arising from these Terms or your use of the Service shall not exceed the amount you paid to us in the 12 months preceding the claim.
              </p>
              <p className="legal-p">
                The Service is provided "as is" and "as available" without warranties of any kind, express or implied.
              </p>
            </section>

            <section id="law">
              <h2 className="legal-h2">12. Governing Law</h2>
              <p className="legal-p">
                These Terms are governed by and construed in accordance with the laws of the Republic of Kenya. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Nairobi, Kenya.
              </p>
              <p className="legal-p">
                We reserve the right to update these Terms at any time. We will notify you of material changes via email or a prominent notice in the Service. Your continued use after changes take effect constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section id="contact">
              <h2 className="legal-h2">13. Contact</h2>
              <p className="legal-p">
                If you have questions about these Terms, contact us at:
              </p>
              <div style={{ background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.06)', padding: '20px', fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#b0b0b0', lineHeight: 1.75 }}>
                <strong style={{ color: '#e5e5e5' }}>LAJ House Limited</strong><br />
                Registration: PVT-3B15M26E<br />
                Nairobi, Kenya<br />
                Email: <a href="mailto:jamdany@lajhouseltd.com" style={{ color: '#E8A020', textDecoration: 'none' }}>jamdany@lajhouseltd.com</a>
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid rgba(232,160,32,0.1)', marginTop: '64px', paddingTop: '24px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <a href="/privacy" style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.7rem', color: '#606060', textDecoration: 'none', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            onMouseOver={e => e.target.style.color='#E8A020'} onMouseOut={e => e.target.style.color='#606060'}>
            Privacy Policy
          </a>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', fontFamily: 'Courier Prime, monospace', fontSize: '0.7rem', color: '#606060', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', padding: 0 }}
            onMouseOver={e => e.target.style.color='#E8A020'} onMouseOut={e => e.target.style.color='#606060'}>
            Home
          </button>
          <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.7rem', color: '#404040', marginLeft: 'auto', letterSpacing: '0.05em' }}>
            © 2026 LAJ House Limited
          </p>
        </div>
      </div>
    </div>
  );
}
